// src/components/reports/ReportCard.js
import React, { useState } from 'react';

const ReportCard = ({ report, onView, onExport }) => {
    const [showExportMenu, setShowExportMenu] = useState(false);

    const handleExport = (format) => {
        onExport(format);
        setShowExportMenu(false);
    };

    return (
        <div className="report-card">
            <div className="report-card-header">
                <div className="report-icon">
                    <i className={report.icon}></i>
                </div>
                <div className="report-info">
                    <h3>{report.title}</h3>
                    <p>{report.description}</p>
                </div>
            </div>

            {report.stats && Object.keys(report.stats).length > 0 && (
                <div className="report-stats">
                    {Object.entries(report.stats).map(([key, value]) => (
                        <div key={key} className="stat-item">
                            <span className="stat-label">{formatStatLabel(key)}:</span>
                            <span className="stat-value">{value}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="report-actions">
                <button
                    className="btn btn-primary"
                    onClick={onView}
                >
                    <i className="fas fa-eye"></i>
                    View Report
                </button>

                <div className="export-dropdown">
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowExportMenu(!showExportMenu)}
                    >
                        <i className="fas fa-download"></i>
                        Export
                        <i className={`fas fa-chevron-${showExportMenu ? 'up' : 'down'}`}></i>
                    </button>

                    {showExportMenu && (
                        <div className="export-menu">
                            <button onClick={() => handleExport('csv')}>
                                <i className="fas fa-file-csv"></i>
                                Export as CSV
                            </button>
                            <button onClick={() => handleExport('pdf')}>
                                <i className="fas fa-file-pdf"></i>
                                Export as PDF
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .report-card {
                    background: var(--white);
                    border-radius: var(--radius-xl);
                    box-shadow: var(--shadow-md);
                    padding: var(--spacing-xl);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    border: 1px solid rgba(0, 0, 0, 0.05);
                }

                .report-card:hover {
                    transform: translateY(-4px);
                    box-shadow: var(--shadow-lg);
                }

                .report-card-header {
                    display: flex;
                    align-items: flex-start;
                    gap: var(--spacing-md);
                    margin-bottom: var(--spacing-lg);
                }

                .report-icon {
                    background: linear-gradient(135deg, var(--admin-primary), var(--admin-secondary));
                    color: white;
                    width: 50px;
                    height: 50px;
                    border-radius: var(--radius-lg);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                    flex-shrink: 0;
                }

                .report-info h3 {
                    color: var(--text-dark);
                    font-size: 1.2rem;
                    font-weight: 600;
                    margin: 0 0 var(--spacing-sm) 0;
                    line-height: 1.3;
                }

                .report-info p {
                    color: var(--text-light);
                    font-size: 0.9rem;
                    margin: 0;
                    line-height: 1.4;
                }

                .report-stats {
                    background: rgba(var(--admin-primary-rgb), 0.03);
                    padding: var(--spacing-md);
                    border-radius: var(--radius-md);
                    margin-bottom: var(--spacing-lg);
                }

                .stat-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: var(--spacing-sm) 0;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
                }

                .stat-item:last-child {
                    border-bottom: none;
                }

                .stat-label {
                    color: var(--text-light);
                    font-size: 0.85rem;
                    text-transform: capitalize;
                }

                .stat-value {
                    color: var(--text-dark);
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .report-actions {
                    display: flex;
                    gap: var(--spacing-sm);
                }

                .export-dropdown {
                    position: relative;
                    flex: 1;
                }

                .export-dropdown .btn {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: var(--spacing-sm);
                }

                .export-menu {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: var(--white);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                    border-radius: var(--radius-md);
                    box-shadow: var(--shadow-lg);
                    z-index: 10;
                    margin-top: var(--spacing-xs);
                    overflow: hidden;
                }

                .export-menu button {
                    width: 100%;
                    padding: var(--spacing-sm) var(--spacing-md);
                    background: none;
                    border: none;
                    text-align: left;
                    color: var(--text-dark);
                    font-size: 0.85rem;
                    cursor: pointer;
                    transition: background-color 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-sm);
                }

                .export-menu button:hover {
                    background: var(--light-color);
                }

                .export-menu button i {
                    width: 16px;
                    color: var(--text-light);
                }

                .btn {
                    padding: var(--spacing-sm) var(--spacing-md);
                    border: none;
                    border-radius: var(--radius-md);
                    font-size: 0.85rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: var(--spacing-xs);
                    flex: 1;
                    justify-content: center;
                }

                .btn-primary {
                    background: var(--admin-primary);
                    color: white;
                }

                .btn-primary:hover {
                    background: var(--admin-secondary);
                    transform: translateY(-1px);
                }

                .btn-secondary {
                    background: var(--light-color);
                    color: var(--text-dark);
                    border: 1px solid rgba(0, 0, 0, 0.1);
                }

                .btn-secondary:hover {
                    background: #e9ecef;
                }

                @media (max-width: 768px) {
                    .report-card-header {
                        flex-direction: column;
                        text-align: center;
                    }

                    .report-actions {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

// Helper function to format stat labels
const formatStatLabel = (key) => {
    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
};

export default ReportCard;
